import { useState } from 'react'

import { Button } from '@ui/components/shadcn/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/shadcn/ui/dialog'
import { Label } from '@ui/components/shadcn/ui/label'
import { Switch } from '@ui/components/shadcn/ui/switch'
import {
  ACCEPT_ALL,
  decideConsent,
  hasGpcSignal,
  readEffectiveConsent,
  REJECT_ALL,
  type ConsentDecisionAction,
  type ConsentState,
} from '@ui/lib/consent'

interface CookiePreferencesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // A decision that changes nothing doesn't reload; the owner may still care.
  onDecided?: () => void
}

export function CookiePreferencesDialog({
  open,
  onOpenChange,
  onDecided,
}: CookiePreferencesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Unmounted while closed, so the draft resets on every open. */}
        <PreferencesForm
          onDone={() => {
            onDecided?.()
            onOpenChange(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

function Row({
  id,
  title,
  description,
  children,
}: {
  id: string
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>{title}</Label>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      {children}
    </div>
  )
}

function PreferencesForm({ onDone }: { onDone: () => void }) {
  // Mounts on open, client-only, so the cookie is readable here.
  const [draft, setDraft] = useState<ConsentState>(readEffectiveConsent)
  const gpc = hasGpcSignal()

  const decide = async (state: ConsentState, action: ConsentDecisionAction) => {
    await decideConsent({ state, action })
    onDone()
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Your Privacy Choices</DialogTitle>
        <DialogDescription>
          Strictly necessary and analytics cookies are always on. Advertising is
          your choice.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-5">
        <Row
          id="consent-necessary"
          title="Strictly Necessary"
          description="Required for the site to work."
        >
          <Switch id="consent-necessary" checked disabled />
        </Row>
        <Row
          id="consent-analytics"
          title="Analytics"
          description="Helps us understand how the site is used."
        >
          <Switch id="consent-analytics" checked disabled />
        </Row>
        <Row
          id="consent-advertising"
          title="Advertising"
          description="Turning this off is a Do Not Sell or Share request."
        >
          <Switch
            id="consent-advertising"
            checked={draft.advertising}
            onCheckedChange={(advertising) => setDraft({ advertising })}
          />
        </Row>
        {gpc && (
          <p className="text-muted-foreground text-xs">
            Your browser sent a Global Privacy Control signal. We treat it as a
            Do Not Sell or Share request unless you choose otherwise here.
          </p>
        )}
      </div>
      <DialogFooter className="sm:justify-center">
        <Button variant="link" onClick={() => decide(draft, 'preferencesSave')}>
          Save Preferences
        </Button>
        <Button
          variant="link"
          onClick={() => decide(REJECT_ALL, 'preferencesRejectAll')}
        >
          Reject All
        </Button>
        <Button
          variant="link"
          onClick={() => decide(ACCEPT_ALL, 'preferencesAcceptAll')}
        >
          Accept All
        </Button>
      </DialogFooter>
    </>
  )
}
